import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { NetworkTemplateService } from "@services/contentNetwork/networkTemplates/networkTemplateService";
import { NetworkAccountService } from "src/shared/services/contentNetwork/networkAccounts/networkAccountService";
import { NetworkResource } from "@models/networkResource";
import type { NetworkTemplate } from "@models/networkTemplate.model";

interface Props {
  onClose: () => void;
  onImported: (template: NetworkTemplate) => void;
}

export const ImportNetworkTemplateModal = ({ onClose, onImported }: Props) => {
  const [accountResources, setAccountResources] = useState<NetworkResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [loadingResources, setLoadingResources] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  /* The import calls LinkedIn live, so it fails for reasons only the server knows: an id that
     is a campaign rather than a campaign group, an account whose token has expired, a campaign
     group in an account we are not connected to. Every one of those was going to `console.error`
     and nowhere else -- the modal stayed open, the spinner stopped, and nothing said why
     (Megan, 19 Oct 2026: "now i can't even get the template to save"). */
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAccountResources = async () => {
      try {
        const resources = await new NetworkAccountService().listNetworkResources({
          organization_ids: [4],
          types: ["account"],
        });
        setAccountResources(resources);
        if (resources.length === 1) setSelectedResource(resources[0].id);
      } catch (err) {
        setError(err instanceof Error
          ? `Could not load the network accounts: ${err.message}`
          : "Could not load the network accounts.");
      } finally {
        setLoadingResources(false);
      }
    };
    loadAccountResources();
  }, []);

  const canSubmit = !!selectedResource && !!name.trim();

  const handleImport = async () => {
    if (!canSubmit) return;
    const resource = accountResources.find((r) => r.id === selectedResource);
    if (!resource) return;

    setSubmitting(true);
    setError(null);
    try {
      const template = await new NetworkTemplateService().import({
        network: resource.network,
        name: name.trim(),
        network_resource_id: selectedResource!,
        source_campaign_id: campaignId.trim() || undefined,
      });
      onImported(template);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The import failed and gave no reason.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Network Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div className="alert alert-danger py-2 small" role="alert">{error}</div>
        )}
        {loadingResources ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Form>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>Name</Form.Label>
              <Col sm={8}>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  placeholder="Template name"
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>Network Account</Form.Label>
              <Col sm={8}>
                <Form.Select
                  value={selectedResource ?? ""}
                  onChange={(e) => setSelectedResource(Number(e.currentTarget.value))}
                >
                  <option value="">Select account…</option>
                  {accountResources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name} ({resource.network})
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>Source Campaign Group ID</Form.Label>
              <Col sm={8}>
                <Form.Control
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.currentTarget.value)}
                  placeholder="Optional — leave blank for an empty template"
                />
                {/* It asks for a campaign GROUP, and a campaign id fails every time. The label
                    said "Campaign ID", which is the wrong id and the easy mistake to make. */}
                <Form.Text className="text-muted">
                  A campaign <strong>group</strong> id, not a campaign id. Leave blank on
                  LinkedIn unless you want to copy bid strategy or geo/language targeting —
                  an empty LinkedIn template is correct and the defaults are filled in for you.
                </Form.Text>
              </Col>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={!canSubmit || submitting}>
          {submitting ? "Importing…" : "Import Template"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
